import React from 'react';
import PropTypes from 'prop-types';
import { Attributes } from '@leverege/ui-attributes';
import { ValueCache } from '@leverege/value-cache';
import { Form, Button, Content, Pane } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { Config } from '@leverege/plugin';
import I18N from '@leverege/i18n';
import { BlueprintAttributeFormValidatorFactory } from "./validators/Factory.js";
import { BlueprintAttributeFormEditorFactory } from "./editors/Factory.js";
import S from './CreateEditForm.css';
import FormUtils from "./Utils.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.forms.CreateEditForm');
export default class CreateEditForm extends React.Component {
  static propTypes = {
    getAttributes: PropTypes.func,
    titleVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    titleIcon: PropTypes.string,
    eventData: PropTypes.any,
    submitText: PropTypes.string,
    cancelText: PropTypes.string
  };
  static defaultProps = {
    getAttributes: FormUtils.defaultGetAttributes,
    titleVariant: 'dialogTitle',
    get title() {
      return t('title');
    },
    get titleIcon() {
      return tIcon('titleIcon', 'fa fa-pencil fa-fw');
    },
    eventData: null,
    get submitText() {
      return t('submitText');
    },
    get cancelText() {
      return t('cancelText');
    }
  };
  constructor(props) {
    super(props);
    this.state = {
      breakpoint: this.getBreakPoint(window?.innerWidth)
    };
    this.cache = new ValueCache();
  }
  getBreakPoint(size) {
    return size > Config.get('ResponsiveUtility', 'smallBreakpoint', 1024) ? 'big' : 'small';
  }
  onResize = evt => {
    this.setState(state => {
      const newState = this.getBreakPoint(evt.target?.innerWidth);
      if (newState !== state.breakpoint) {
        return {
          breakpoint: newState
        };
      }
      return null;
    });
  };
  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }
  onSubmit = evt => {
    const {
      onSubmit,
      eventData,
      target
    } = this.props;
    const {
      value
    } = evt;
    const toSend = {};
    const data = [];
    const nameAttr = Attributes.getAttribute('name', target?.type);
    const idAttrs = this.getAttributes().reduce((acc, curr) => {
      const externalId = curr.blueprint?.attribute?.getTagStartingWith?.('externalId:', true);
      if (externalId) {
        acc[curr?.blueprint?.attribute?.path] = externalId.split(':');
      }
      return acc;
    }, {});
    const resourceImageAttrs = this.getAttributes().filter(item => {
      return item.valueType === 'resourceImage';
    });
    const imagesToCreate = [];
    let nameKey = nameAttr?.blueprint?.attribute?.path;
    if (nameKey) {
      nameKey = `data/${nameKey}`;
    }
    Object.entries(value).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      if (key.startsWith('data/')) {
        const isResourceImage = resourceImageAttrs.find(attr => attr.blueprint.field === key);
        if (isResourceImage && Array.isArray(value)) {
          if (value[0] instanceof File) {
            imagesToCreate.push({
              path: `resourceImage/${key.slice(5)}`,
              file: value,
              updatePath: key.slice(5)
            });
            data.push({
              path: key.slice(5),
              value: value[0].name
            });
          }
          return;
        }
        data.push({
          path: key.slice(5),
          value
        });
        const externalIdKeys = idAttrs[key.slice(5)];
        if (externalIdKeys) {
          if (!toSend.networkAliases) {
            toSend.networkAliases = {};
          }
          if (!toSend.networkAliases[externalIdKeys[0]]) {
            toSend.networkAliases[externalIdKeys[0]] = {};
          }
          toSend.networkAliases[externalIdKeys[0]][externalIdKeys[1]] = value;
          toSend.networkId = externalIdKeys[0];
        }

        // This will set the device name to be attribute 'name'
        if (nameKey === key) {
          toSend.name = value;
        }
      } else if (key.startsWith('header/')) {
        toSend[key.slice(7)] = value;
      }
    });
    if (data.length > 0) {
      toSend.data = data;
    }
    if (onSubmit) onSubmit({
      value: toSend,
      data: eventData,
      original: evt,
      imagesToCreate
    });
  };
  onCancel = evt => {
    const {
      onCancel,
      eventData
    } = this.props;
    if (onCancel) onCancel({
      data: eventData,
      original: evt
    });
  };
  getAttributes() {
    const {
      getAttributes,
      target
    } = this.props;
    return this.cache.calc((target, getAttributes) => {
      const attrs = getAttributes(target, FormUtils.filterAttributeFilterTag(target?.id ? 'update:exclude' : 'create:exclude')) || [];
      // sort them
      attrs.sort(FormUtils.sort);
      return attrs;
    }, target, getAttributes);
  }
  buildValidator() {
    const attributes = this.getAttributes();
    const validators = {
      fields: {}
    };
    attributes.forEach(attr => {
      let validatorType;
      const validatorTag = attr.blueprint?.attribute?.getTagStartingWith?.('formValidator:', true);
      if (validatorTag) {
        validatorType = validatorTag;
      } else if (attr.valueType === 'resourceImage') {
        validatorType = 'image';
      } else if (attr.blueprint?.attribute?.hasTag?.('is:email')) {
        validatorType = 'email';
      } else if (attr.blueprint?.attribute?.hasTag?.('is:phone')) {
        validatorType = 'phone';
      } else {
        validatorType = attr.name;
      }
      const field = attr?.blueprint?.updatePath;
      const validator = BlueprintAttributeFormValidatorFactory.create(validatorType, attr);
      if (validator) {
        validators.fields[field] = validator;
      }
    });
    return validators;
  }
  getData() {
    const {
      target
    } = this.props;
    const data = target?.data;
    const attributes = this.getAttributes();
    const toReturn = {};
    attributes.forEach(attr => {
      const key = attr?.blueprint?.updatePath;
      if (key && attr?.blueprint?.renamed !== true && attr?.valueType !== 'parentRelationship') {
        toReturn[key] = attr.get(data);
        if (attr?.valueType === 'resourceImage' && toReturn[key]) {
          toReturn[key] = [{
            name: attr.get(data)
          }];
        }
        if (toReturn[key] && attr.blueprint?.attribute?.isUnit()) {
          toReturn[key] = toReturn[key].value;
        }
      }
    });
    return toReturn;
  }
  renderForm = validator => {
    const {
      titleVariant,
      title,
      titleIcon,
      submitText,
      cancelText,
      headerVariant,
      bodyVariant,
      footerVariant
    } = this.props;
    const {
      breakpoint
    } = this.state;
    const attributes = this.getAttributes();
    const editors = attributes.map(attr => {
      const tag = attr.blueprint?.attribute?.getTagStartingWith?.('formEditor:', true);
      let editor = tag && BlueprintAttributeFormEditorFactory.create(tag, {
        ...this.props,
        attr,
        key: attr.name
      });
      if (editor == null) {
        editor = BlueprintAttributeFormEditorFactory.create(attr, {
          attr,
          key: attr.name,
          ...this.props
        });
      }
      return editor;
    });
    const headerVnt = headerVariant || (breakpoint === 'small' ? 'mobileItemFormHeader' : 'itemFormHeader');
    const bodyVnt = bodyVariant || (breakpoint === 'small' ? 'mobileItemFormBody' : 'itemFormBody');
    const footerVnt = footerVariant || (breakpoint === 'small' ? 'mobileItemFormFooter' : 'itemFormFooter');
    return /*#__PURE__*/_jsxs(Content, {
      relative: breakpoint === 'big',
      children: [/*#__PURE__*/_jsx(Content.Header, {
        variant: `${headerVnt}|formHeader`,
        children: /*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: title,
          icon: titleIcon
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        variant: `${bodyVnt}|formBody`,
        className: S.form,
        children: editors
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        variant: `${footerVnt}|formButtons`,
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          type: "button",
          onClick: this.onCancel,
          children: cancelText
        }), /*#__PURE__*/_jsx(Form.Submit, {
          variant: "primary",
          children: submitText
        })]
      })]
    });
  };
  render() {
    const {
      variant
    } = this.props;
    const {
      breakpoint
    } = this.state;
    const vnt = variant || (breakpoint === 'small' ? 'mobileItemForm' : 'itemForm');
    return /*#__PURE__*/_jsx(Pane, {
      variant: vnt,
      children: /*#__PURE__*/_jsx(Form, {
        className: S.fixed,
        data: this.getData() || {},
        onSubmit: this.onSubmit,
        render: this.renderForm,
        validator: this.buildValidator()
      })
    });
  }
}